// Public navigation / route registry — SINGLE SOURCE OF TRUTH for public route STRUCTURE.
// Task: nav/route consolidation (precursor to WP-2 Phase B). Governed by ADR-0008 (§7 SoT,
// §7A entity model) and ADR-0007 (§12.8 SEO content static, not runtime i18n).
//
// OWNS: path, indexable flag, Header/Footer membership, the i18n label KEY for nav rendering,
//       the STATIC FR breadcrumb label (SEO), sitemap inclusion/priority, cross-canonical link.
// DOES NOT OWN: SEO copy (seo/seoData.js, keyed by seoKey), localized strings (locales/*.json),
//       routing (App.js). Only a pure-data import (serviceCatalog) → still node-importable by the
//       sitemap generator.
//
// CONSUMERS: Header/Footer (components/Layout.js), C1B breadcrumb/INDEXABLE (seo/jsonld.js),
//       sitemap generator (scripts/generate-sitemap.mjs), future Prerender Registry (Phase B).
//
// ARRAY ORDER is authored so a single list satisfies every consumer by filtering:
//   Header / Footer-nav : home, services, entreprises, reserver, contact
//   Sitemap (filter)    : home, services, entreprises, contact, confidentialite, cgu,
//                         mentions-legales, refund-policy   (byte-identical to the prior static file)
//   Footer-legal (filter): confidentialite, cgu, mentions-legales, refund-policy, chauffeur-reglement

import { SERVICE_CATALOG, servicePath, serviceSeoKey } from './serviceCatalog.js';

const STATIC_ROUTES = [
  { path: '/', seoKey: 'home', indexable: true, header: true, footer: 'nav',
    navLabelKey: 'nav.home', breadcrumbLabel: null, sitemap: { include: true, priority: '1.0' } },
  { path: '/services', seoKey: 'services', indexable: true, header: true, footer: 'nav',
    navLabelKey: 'nav.services', breadcrumbLabel: 'Services', sitemap: { include: true, priority: '0.9' } },
  { path: '/entreprises', seoKey: 'entreprises', indexable: true, header: true, footer: 'nav',
    navLabelKey: 'nav.enterprises', breadcrumbLabel: 'Entreprises', sitemap: { include: true, priority: '0.8' } },
  { path: '/reserver', seoKey: 'reserver', indexable: false, header: true, footer: 'nav',
    navLabelKey: 'nav.book', breadcrumbLabel: null, sitemap: { include: false } },
  { path: '/contact', seoKey: 'contact', indexable: true, header: true, footer: 'nav',
    navLabelKey: 'nav.contact', breadcrumbLabel: 'Contact', sitemap: { include: true, priority: '0.6' } },
  { path: '/confidentialite', seoKey: 'confidentialite', indexable: true, header: false, footer: 'legal',
    navLabelKey: 'footer.privacy', breadcrumbLabel: 'Politique de confidentialité', sitemap: { include: true, priority: '0.2' } },
  { path: '/legal/cgu', seoKey: 'cgu', indexable: true, header: false, footer: 'legal',
    navLabelKey: 'footer.termsOfUse', breadcrumbLabel: "Conditions d'utilisation", sitemap: { include: true, priority: '0.3' } },
  { path: '/legal/mentions-legales', seoKey: 'mentionsLegales', indexable: true, header: false, footer: 'legal',
    navLabelKey: 'footer.legalNotice', breadcrumbLabel: 'Mentions légales', sitemap: { include: true, priority: '0.2' } },
  { path: '/legal/refund-policy', seoKey: 'refundPolicy', indexable: true, header: false, footer: 'legal',
    navLabelKey: 'footer.refundPolicy', breadcrumbLabel: 'Politique de remboursement', sitemap: { include: true, priority: '0.2' } },
  // Company section (footer:'company') — Phase 3 About; future Careers / Partners / Sustainability slot
  // in here with no footer redesign. Placed after the prior 8 sitemap routes to preserve their order.
  { path: '/about', seoKey: 'about', indexable: true, header: false, footer: 'company',
    navLabelKey: 'footer.about', breadcrumbLabel: 'À propos', sitemap: { include: true, priority: '0.5' } },
  { path: '/legal/chauffeur-reglement', seoKey: 'chauffeurReglement', indexable: false, header: false, footer: 'legal',
    navLabelKey: 'footer.driverRules', breadcrumbLabel: null, sitemap: { include: false } },
  // Legacy cross-canonical alias of /legal/mentions-legales: indexable for C1A (no noindex) but emits
  // NO JSON-LD and is NOT in the sitemap (crossCanonicalTo excludes it from JSON-LD/sitemap derivations).
  { path: '/mentions-legales', seoKey: 'mentionsLegalesLegacy', indexable: true, header: false, footer: false,
    navLabelKey: null, breadcrumbLabel: null, crossCanonicalTo: '/legal/mentions-legales', sitemap: { include: false } },
];

// WP-3.3 — per-service routes derived from the ratified SVC-2 catalogue (single source). Indexable +
// in the sitemap; NOT in header/footer nav (reached via the /services hub + breadcrumbs). Wedding
// Chauffeur is absent from the catalogue, so it can never appear here.
const SERVICE_ROUTES = SERVICE_CATALOG.map((s) => ({
  path: servicePath(s.slug),
  seoKey: serviceSeoKey(s.slug),
  indexable: true,
  header: false,
  footer: false,
  navLabelKey: null,
  breadcrumbLabel: s.name,
  sitemap: { include: true, priority: '0.7' },
}));

export const ROUTES = [...STATIC_ROUTES, ...SERVICE_ROUTES];

// ---- Derived selectors (computed once) ----------------------------------------------------------
export const HEADER_ROUTES = ROUTES.filter((r) => r.header);
export const FOOTER_NAV_ROUTES = ROUTES.filter((r) => r.footer === 'nav');
export const FOOTER_COMPANY_ROUTES = ROUTES.filter((r) => r.footer === 'company');
export const FOOTER_LEGAL_ROUTES = ROUTES.filter((r) => r.footer === 'legal');
export const SITEMAP_ROUTES = ROUTES.filter((r) => r.sitemap && r.sitemap.include);

// JSON-LD / breadcrumb surface = indexable AND self-canonical (the legacy cross-canonical alias is excluded).
const JSONLD_ROUTES = ROUTES.filter((r) => r.indexable && !r.crossCanonicalTo);
export const INDEXABLE = Object.fromEntries(JSONLD_ROUTES.map((r) => [r.path, r.seoKey]));
export const BREADCRUMB_LABEL = Object.fromEntries(
  JSONLD_ROUTES.filter((r) => r.breadcrumbLabel).map((r) => [r.path, r.breadcrumbLabel]),
);
